import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Truck, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("processing"); // processing | error

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Aucun identifiant de session Google trouvé dans l'URL. Veuillez réessayer.");
      setStatus("error");
      return;
    }
    const session_id = decodeURIComponent(match[1]);
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        setUser(data);
        // clear hash so a refresh doesn't re-process the session
        window.history.replaceState({}, document.title, window.location.pathname);
        // double-check the session is active server-side (cookie stored)
        const fresh = await refresh();
        if (!fresh) {
          setError(
            "Votre navigateur a refusé d'enregistrer le cookie de session. Activez les cookies tiers ou désactivez les bloqueurs de traceurs pour ce site, puis réessayez."
          );
          setStatus("error");
          return;
        }
        if (fresh.needs_role_selection) navigate("/onboarding", { replace: true });
        else navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Google callback failed", err);
        setError(formatApiError(err.response?.data?.detail) || err.message || "Erreur de connexion Google.");
        setStatus("error");
      }
    })();
  }, [navigate, setUser, refresh]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="auth-callback-error">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-red-500" />
          <h1 className="font-display font-bold text-2xl text-slate-900 mt-3">Connexion Google échouée</h1>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">{error}</p>
          <div className="mt-6 flex flex-col gap-2">
            <Link to="/login"><Button className="w-full bg-orange-600 hover:bg-orange-700">Retour à la connexion</Button></Link>
            <Link to="/register"><Button variant="outline" className="w-full">Créer un compte</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center bg-orange-600 text-white rounded-md animate-pulse">
          <Truck className="h-6 w-6" />
        </span>
        <div className="mt-4 text-slate-700 font-medium">Connexion Google en cours…</div>
      </div>
    </div>
  );
}
